/**
 * Artikeldetail -- die Arbeitsseite einer SKU.
 *
 *   Produktkopf      stabil, bei jedem Bereichswechsel
 *   Subnavigation    Uebersicht · Technik · Logistik · Texte · Dokumente ·
 *                    Einkauf · Modell · Herkunft
 *   Inhalt           wechselt
 *
 * DIE UEBERSICHT IST KEINE FEHLERLISTE.
 *
 * Bis V1 begruesste sie mit vier Warnhinweisen: widerspruechliche Merkmale,
 * Merkmale ausserhalb des Modells, offene Pruefhinweise, offene
 * Beschaffungsangaben. Fachlich richtig, an dieser Stelle falsch -- dieselbe
 * Seite soll auf einem Tablet vor einem Kunden stehen koennen, und sie darf
 * ein Produkt nicht als fehlerhaft erscheinen lassen, nur weil intern noch
 * Datenpflege aussteht.
 *
 * Die Hinweise sind nicht geloescht. Jeder steht jetzt in dem Bereich, in dem
 * er BEARBEITET werden kann -- ein Konflikt in Technik, eine fehlende
 * Mindestabnahme im Einkauf, eine fehlende Zolltarifnummer in Logistik. Der
 * Server sagt fuer jeden Punkt, wohin er gehoert; diese Datei verteilt ihn
 * nur.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ArticleDetail as Detail, Capability, MetaResponse,
              ProductSectionCode, ResolvedText } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { ErrorState, LoadingState, Unknown } from "./States.tsx";
import { ArticleIdentity, OperativeZeile } from "./ArticleOverview.tsx";
import { ProductSubnav, visibleSections } from "./ProductSubnav.tsx";
import { SectionAttention } from "./SectionAttention.tsx";
import { ArticleTexts } from "./ArticleTexts.tsx";
import { ArticleTechnik, type TechnikSteuerung } from "./ArticleTechnik.tsx";
import { ArticleDocuments, type DokumenteSteuerung } from "./ArticleDocuments.tsx";
import { ArticleModelSection } from "./ArticleModel.tsx";
import { CalculationSections, ImportwegPreise, TrashAction } from "./ProductDossier.tsx";
import { ArticlePackagingSection, ArticlePurchasingSection,
         type LogistikSteuerung } from "./ArticleLogistics.tsx";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { categoryLabel } from "../i18n/terminology.ts";
import "./ArticleDetail.css";

/**
 * „Darf ich hier weg?" -- die Frage, die ein Rahmen stellen muss.
 *
 * Ein Reiterwechsel, ein anderer Artikel und das Schliessen sehen fuer den
 * Benutzer gleich harmlos aus; stehen im Bearbeitungsmodus ungespeicherte
 * Eingaben, ist jeder davon ein Datenverlust. Die Ansicht kann das nicht
 * allein loesen: der Artikelwechsel passiert in der LISTE, nicht hier.
 *
 * Deshalb dieser eine Typ. Wer einen Wechsel ausloest, ruft nicht die
 * Handlung, sondern uebergibt sie -- und sie laeuft, sobald die Frage geklaert
 * ist. Ist nichts offen, laeuft sie sofort.
 */
export type LeaveGuard = (proceed: () => void) => void;

export function ArticleDetailPanel({ id, onClose, capabilities, mayWrite, mayRelease, meta, mayWriteProps, mayConfirmProps, mayWriteModels, mayListDte, mayWriteLogistics, mayConfirmLogistics, onOpenModel, onOpenArticle, onOpenSupplier, onOpenDossier, onTrashed, guardRef }: {
  id: number;
  onClose: () => void;
  /** Einen ANDEREN Artikel oeffnen -- etwa den verbauten Treiber. */
  onOpenArticle: (id: number) => void;
  /**
   * Die Faehigkeiten des angemeldeten Nutzers -- sie entscheiden, welche
   * Bereiche die Subnavigation ueberhaupt anbietet. Sie entscheiden NICHT
   * ueber den Zugriff; das tut der Server.
   */
  capabilities: readonly Capability[];
  /** Die Oberflaeche richtet sich danach; entschieden wird im Server. */
  mayWrite: boolean;
  mayRelease: boolean;
  meta: MetaResponse;
  mayWriteProps: boolean;
  mayConfirmProps: boolean;
  mayWriteModels: boolean;
  /** Eigenes Recht: die Listung wirkt in ein Fremdsystem. */
  mayListDte: boolean;
  /** Eigene Rechte der Logistikebene -- Erfassen und fachlich Bestaetigen. */
  mayWriteLogistics: boolean;
  mayConfirmLogistics: boolean;
  onOpenModel: (id: number) => void;
  /** Ins Lieferantenportal, zu genau diesem Lieferanten. */
  onOpenSupplier?: (id: number, fokus?: "bewertung") => void;
  /**
   * Die zentrale Produktakte oeffnen -- die durchgehende Pflegeflaeche.
   *
   * `undefined`, wenn der Nutzer nicht pflegen darf. Die Arbeitsansicht
   * bleibt fuer ihn vollstaendig; nur der Weg in die Akte fehlt.
   */
  onOpenDossier?: (id: number) => void;
  /**
   * Den geoeffneten Artikel in den Papierkorb legen (Papierkorb 001).
   *
   * Dieselbe Aktion wie in der zentralen Produktakte -- dieselbe Komponente,
   * dieselbe API, derselbe Papierkorb. Neu ist nur, dass sie auch HIER
   * erreichbar ist: wer einen Artikel offen hat, soll ihn nicht erst in einer
   * anderen Ansicht suchen muessen. Fehlt der Rueckruf, fehlt die Aktion.
   */
  onTrashed?: () => void;
  /**
   * Wird mit der Rueckfrage belegt, solange dieses Detail offen ist.
   *
   * Der Rahmen (App) fuehrt den Artikelwechsel aus und fragt hierueber
   * vorher nach. Ohne diese Bruecke waere der Wechsel aus der Liste der
   * einzige Weg, an dem Eingaben verschwinden koennten.
   */
  guardRef?: React.MutableRefObject<LeaveGuard | null>;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  /*
   * Dokumente haben seit 0056 ihr eigenes Recht. Es wird hier aus den
   * ohnehin uebergebenen Faehigkeiten abgeleitet und nicht als weiterer
   * Schalter durch drei Ebenen gereicht -- wie `price.input.write` weiter
   * unten auch.
   */
  const mayWriteDocuments = capabilities.includes("document.write");
  const [a, setA] = useState<Detail | null>(null);
  const [texts, setTexts] = useState<readonly ResolvedText[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  /*
   * Der Ruhezustand ist ein BEREICH, kein „nichts offen". Das ist der
   * Unterschied zu V1: „Uebersicht" ist ein Navigationsziel wie jedes andere,
   * und der Weg dorthin ist derselbe Klick an derselben Stelle.
   */
  const [open, setOpen] = useState<ProductSectionCode>("technik");
  /*
   * DER BEARBEITUNGSMODUS -- er gehoert dem RAHMEN, nicht dem Inhalt.
   *
   * Der Stift steht in der Bereichsleiste, der bearbeitete Inhalt eine Ebene
   * tiefer. Laege der Zustand unten, muesste die Leiste ihn erraten; laege er
   * an zwei Stellen, liefen beide auseinander. Er steht deshalb hier -- an
   * derselben Stelle wie der geoeffnete Bereich, denn es ist dieselbe Art von
   * Zustand: WO bin ich, und WAS tue ich dort.
   *
   * Heute schaltet er ausschliesslich die Technik. Ein weiterer Bereich mit
   * Bearbeitung bekommt spaeter denselben Schalter -- vorweggenommen wird
   * hier nichts.
   */
  const [technikEdit, setTechnikEdit] = useState(false);
  const technik = useRef<TechnikSteuerung | null>(null);
  /*
   * Die Logistik bekommt denselben Schalter -- nicht einen aehnlichen.
   *
   * „Ein weiterer Bereich mit Bearbeitung bekommt spaeter denselben
   * Schalter" stand hier als Absichtserklaerung. Dies ist dieser Bereich.
   */
  const [logistikEdit, setLogistikEdit] = useState(false);
  const logistik = useRef<LogistikSteuerung | null>(null);
  const [einkaufEdit, setEinkaufEdit] = useState(false);
  const einkauf = useRef<LogistikSteuerung | null>(null);
  /*
   * Und die Dokumente denselben. Offen ist dort genau eine Sache -- eine
   * gewaehlte Datei, die noch nicht hochgeladen ist. Das sieht nach nichts
   * aus und ist der Weg, auf dem eine ausgesuchte Datei samt Stand und
   * Sprache bei einem Reiterwechsel verschwindet.
   */
  const [dokumenteEdit, setDokumenteEdit] = useState(false);
  const dokumente = useRef<DokumenteSteuerung | null>(null);
  /** Die Handlung, die auf die Antwort der Rueckfrage wartet. */
  const [wartend, setWartend] = useState<{ run: () => void } | null>(null);

  /*
   * Nichts geht verloren -- auch nicht bei einem Klick, der harmlos aussieht.
   *
   * Sind Eingaben offen, wird die Handlung ANGEHALTEN und die Frage gestellt.
   * Sonst laeuft sie unveraendert durch; ein Dialog, der auch dann erscheint,
   * wenn nichts offen ist, wird nach dem dritten Mal weggeklickt, ohne
   * gelesen zu werden.
   */
  const bewache = useCallback((proceed: () => void): void => {
    /* Offen ist offen -- gleich in welchem Bereich. Die Rueckfrage kennt
       keinen Bereichsnamen; sie fragt, ob etwas verlorenginge. */
    if (technik.current?.dirty || logistik.current?.dirty || einkauf.current?.dirty
        || dokumente.current?.dirty) {
      setWartend({ run: proceed }); return;
    }
    proceed();
  }, []);

  useEffect(() => {
    if (!guardRef) return;
    guardRef.current = bewache;
    return () => { guardRef.current = null; };
  }, [guardRef, bewache]);

  useEffect(() => {
    let cancelled = false;
    setA(null); setTexts(null); setError(null); setOpen("technik");
    setTechnikEdit(false); setLogistikEdit(false); setEinkaufEdit(false);
    setDokumenteEdit(false);
    api.article(id)
      .then((r) => { if (!cancelled) setA(r); })
      .catch((e) => { if (!cancelled) setError(e); });
    // Der Kopf braucht den Artikelnamen und der Ueberblick die beiden
    // Beschreibungen -- beides kommt aus derselben Abfrage.
    api.articleTexts(id)
      .then((r) => { if (!cancelled) setTexts(r.items); })
      .catch(() => { if (!cancelled) setTexts([]); });
    return () => { cancelled = true; };
  }, [id]);

  /* Nach „Neu rechnen" und nach einer Freigabe: nur der Artikel wird neu
     gelesen -- der Kopf zeigt dann den Preis in seinem neuen Zustand. Der
     offene Reiter und seine Eingaben bleiben, wo sie sind. */
  /*
   * GLEICHE KARTENHOEHE OBEN (Importweg-UI 001).
   *
   * Links steht eine wechselnde Zahl von Elementen (Abschnittszeile, Hinweise,
   * eine oder mehrere Lieferantenkarten, „Supplier-Beziehung hinzufuegen") --
   * eine feste Rasterzeile fuer beide Karten waere zerbrechlich. Deshalb
   * werden nur die beiden Hauptkarten angeglichen, und nur solange sie
   * nebeneinander stehen: beide bekommen die Hoehe der hoeheren. Stehen sie
   * untereinander, traegt jede ihre natuerliche Hoehe.
   */
  const obenRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const oben = obenRef.current;
    if (!oben || typeof ResizeObserver === "undefined") return;
    let bild = 0;
    const angleichen = (): void => {
      const l = oben.querySelector<HTMLElement>(".ek__links .ek__karte");
      const r = oben.querySelector<HTMLElement>(".ek__rechts .vergleich--kompakt");
      if (!l || !r) return;
      l.style.minHeight = ""; r.style.minHeight = "";
      if (Math.abs(l.getBoundingClientRect().top - r.getBoundingClientRect().top) > 1) return;
      const h = Math.max(l.offsetHeight, r.offsetHeight);
      l.style.minHeight = `${h}px`; r.style.minHeight = `${h}px`;
    };
    const planen = (): void => { cancelAnimationFrame(bild); bild = requestAnimationFrame(angleichen); };
    const ro = new ResizeObserver(planen);
    ro.observe(oben);
    const mo = new MutationObserver(planen);
    mo.observe(oben, { childList: true, subtree: true });
    planen();
    return () => { ro.disconnect(); mo.disconnect(); cancelAnimationFrame(bild); };
  });
  /* Nach jeder Aenderung der Kalkulation liest „Listenpreis nach Importweg" neu. */
  const [preisVersion, setPreisVersion] = useState(0);
  const kopfNeuLaden = useCallback((): void => {
    api.article(id).then(setA).catch(() => { /* Der Kopf bleibt beim alten Stand. */ });
  }, [id]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") bewache(onClose); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, bewache]);

  const goTo = useCallback((s: ProductSectionCode) => {
    bewache(() => {
      setOpen(s);
      /* Der Bearbeitungsmodus gehoert zu SEINEM Bereich. Wer die Technik
         verlaesst, verlaesst auch ihre Bearbeitung -- ein Stift, der in der
         Logistik weiterleuchtet, verspricht etwas, das dort nicht stattfindet. */
      if (s !== "technik") setTechnikEdit(false);
      if (s !== "logistik") setLogistikEdit(false);
      if (s !== "einkauf") setEinkaufEdit(false);
      if (s !== "dokumente") setDokumenteEdit(false);
      // Erst rendern lassen, dann an den Anfang des Inhalts springen.
      requestAnimationFrame(() => {
        document.querySelector(".detail__content")?.scrollIntoView({ block: "start" });
      });
    });
  }, [bewache]);

  /*
   * Welche Bereiche dieser Nutzer sieht. Ein Bereich, den er nicht sehen
   * soll, ist nicht ausgegraut -- er ist gar nicht da.
   */
  const bereiche = visibleSections(capabilities);

  return (
    <aside className="detail" aria-label={t("detail.aria")}>
      <div className="detail__bar">
        {/* Die Akte ist ein Wechsel der ABSICHT: von „ansehen" zu „pflegen".
            Deshalb eine eigene Schaltflaeche und kein weiterer Reiter -- sie
            fuehrt aus dieser Seite heraus, nicht in ihr herum. */}
        {onOpenDossier && (
          <button className="btn" onClick={() => onOpenDossier(id)}>{t("detail.openDossier")}</button>
        )}
        {/*
          * DESTRUKTIVES LEISE, ABER AUFFINDBAR.
          *
          * Kein roter Knopf neben „Schliessen": ein Fehlklick waere einen
          * Pixel vom Verlassen entfernt. Eingeklappt unter „Weitere
          * Aktionen", und auch dort loest der Knopf nichts aus, sondern
          * oeffnet die Rueckfrage mit Artikelnummer und Grund.
          */}
        {a !== null && onTrashed && mayWrite && (
          <details className="detail__weitere" data-testid="detail-weitere">
            <summary>{t("mp.moreActions")}</summary>
            <TrashAction a={a} meta={meta} i18n={i18n}
              onDone={() => bewache(onTrashed)} onError={setError} />
          </details>
        )}
        <button className="btn" onClick={() => bewache(onClose)}
                aria-label={t("common.close")}>{t("common.close")}</button>
      </div>

      {error !== null && <ErrorState message={errorText(i18n, error)} />}
      {!a && error === null && <LoadingState />}

      {a && (
        <div className="detail__body">
          {/*
            * DER STABILE RAHMEN.
            *
            * Produktkopf und Subnavigation bleiben stehen, der Inhalt darunter
            * wechselt. Wer in der Verpackungshistorie arbeitet, soll nicht
            * raten muessen, an welchem Artikel er das tut.
            */}
          <ArticleIdentity a={a} texts={texts} />
          {/*
            * DIE OPERATIVE ZEILE -- ZWISCHEN IDENTITAET UND ARBEITSBEREICHEN.
            *
            * Bestand, Angebote, Bestellung und Zulauf beschreiben den ARTIKEL,
            * nicht einen seiner Arbeitsbereiche. Sie standen in der
            * Uebersicht und waren damit genau dann fort, wenn man sie
            * braucht: beim Pflegen der Technik, beim Blick in den Einkauf,
            * beim Nachsehen in der Logistik.
            *
            * Sie steht deshalb oberhalb der Bereichsleiste und bleibt auf
            * jedem Reiter stehen -- vier Zahlen in einer Zeile, keine
            * Kennzahlenwand.
            */}
          <OperativeZeile a={a} i18n={i18n} />
          {/*
            * DER EINE STIFT -- am rechten Ende der Bereichsleiste.
            *
            * Er erscheint nur dort, wo er etwas tut, und nur fuer ein Konto,
            * das im JEWEILIGEN Bereich schreiben darf. Ein Stift in einem
            * Bereich ohne Bearbeitung waere eine Ankuendigung; einer fuer
            * einen Leser waere ein Versprechen, das der Server bricht.
            *
            * Zwei Bereiche, EIN Bedienmuster: derselbe Ort, dasselbe Symbol,
            * dieselbe Bedeutung — „diesen Arbeitsbereich bearbeiten". Welcher
            * Bereich gemeint ist, entscheidet der geoeffnete Reiter.
            */}
          <ProductSubnav sections={bereiche} current={open} onSelect={goTo}
            action={
              open === "technik" && mayWriteProps ? (
                <button type="button"
                  className={technikEdit ? "subnav__pencil subnav__pencil--on" : "subnav__pencil"}
                  aria-pressed={technikEdit}
                  title={t("props.edit")}
                  aria-label={t("tk.editAria")}
                  onClick={() => {
                    if (technikEdit) { bewache(() => setTechnikEdit(false)); return; }
                    setTechnikEdit(true);
                  }}>{PENCIL}</button>
              ) : open === "einkauf" && mayWriteLogistics ? (
                <button type="button"
                  className={einkaufEdit ? "subnav__pencil subnav__pencil--on" : "subnav__pencil"}
                  aria-pressed={einkaufEdit}
                  title={t("props.edit")}
                  aria-label={t("ek.editAria")}
                  onClick={() => {
                    if (einkaufEdit) { bewache(() => setEinkaufEdit(false)); return; }
                    setEinkaufEdit(true);
                  }}>{PENCIL}</button>
              ) : open === "logistik" && mayWriteLogistics ? (
                <button type="button"
                  className={logistikEdit ? "subnav__pencil subnav__pencil--on" : "subnav__pencil"}
                  aria-pressed={logistikEdit}
                  title={t("props.edit")}
                  aria-label={t("log.editAria")}
                  onClick={() => {
                    if (logistikEdit) { bewache(() => setLogistikEdit(false)); return; }
                    setLogistikEdit(true);
                  }}>{PENCIL}</button>
              ) : open === "dokumente" && mayWriteDocuments ? (
                /* Derselbe Stift an derselben Stelle -- „diesen Arbeitsbereich
                   bearbeiten". Das Recht ist seit 0056 `document.write` und
                   nicht mehr `article.write`: wer Datenblaetter pflegt, muss
                   Artikelnummer und Familienzuordnung nicht aendern duerfen. */
                <button type="button"
                  className={dokumenteEdit ? "subnav__pencil subnav__pencil--on" : "subnav__pencil"}
                  aria-pressed={dokumenteEdit}
                  title={t("props.edit")}
                  aria-label={t("doc.editAria")}
                  onClick={() => {
                    if (dokumenteEdit) { bewache(() => setDokumenteEdit(false)); return; }
                    setDokumenteEdit(true);
                  }}>{PENCIL}</button>
              ) : undefined} />

          <div className="detail__content">
            {open === "technik" ? (
              <ArticleTechnik articleId={a.id} meta={meta} ausfuehrung={a.variant?.values ?? null}
                mayWrite={mayWriteProps} mayConfirm={mayConfirmProps}
                mayWriteBreaker={capabilities.includes("article.write")}
                editing={technikEdit} onEditingChange={setTechnikEdit}
                steuerung={technik} onOpenArticle={onOpenArticle}
                attention={<SectionAttention items={a.attention} section="technik" i18n={i18n} />} />
            ) : open === "logistik" ? (
              <ArticlePackagingSection articleId={a.id} meta={meta}
                mayWrite={mayWriteLogistics} mayConfirm={mayConfirmLogistics}
                editing={logistikEdit} onEditingChange={setLogistikEdit}
                steuerung={logistik}
                i18nLabel={t("log.sectionPackaging")}
                attention={<SectionAttention items={a.attention} section="logistik" i18n={i18n} />} />
            ) : open === "einkauf" ? (
              <>
                {/*
                  * DER EINKAUF ZEIGT JETZT AUCH DIE KALKULATION.
                  *
                  * Sie existierte bereits vollstaendig -- Zuschlaege,
                  * Rechenweg vom EXW-Preis bis zum Listenpreis, Freigabe --
                  * aber nur auf der durchgehenden Produktakte. Wer im Reiter
                  * „Einkauf" nachsah, fand Supplier, Konditionen und
                  * Lieferantenbewertung, und genau das, wofuer der Einkauf da
                  * ist, fehlte.
                  *
                  * Hier wird NICHTS neu gerechnet und nichts nachgebaut: es
                  * ist dieselbe Komponente auf demselben Kalkulationsstand.
                  * Eine zweite Darstellung waere eine zweite Rechenwahrheit.
                  */}
                {/*
                  * NUR KONFLIKTE, KEINE LUECKEN.
                  *
                  * „Mindestabnahme fehlt" stand als Warnband ueber dem ganzen
                  * Einkauf -- fuer eine Angabe, die zwei Zeilen tiefer
                  * ohnehin als „nicht bestimmt" dasteht. Eine Warnung ist
                  * dafuer da, dass jemand handelt; eine fehlende MOQ
                  * blockiert nichts. Widersprueche bleiben dagegen oben
                  * stehen.
                  */}
                {/*
                  * OBEN DIE ENTSCHEIDUNG, UNTEN DIE HERLEITUNG (Importweg 001).
                  *
                  * Links: Einkauf / Supplier -- was kostet es, bei wem.
                  * Rechts: Listenpreis nach Importweg -- was kostet es den
                  * Kunden, je nachdem, wie schnell er es braucht. Darunter
                  * bleibt die Listenpreiskalkulation: wie der Preis entsteht.
                  */}
                <div ref={obenRef} className={capabilities.includes("purchasing.read") ? "ek__oben" : undefined}>
                <div className="ek__links">
                <ArticlePurchasingSection articleId={a.id} meta={meta}
                  mayWrite={mayWriteLogistics} mayConfirm={mayConfirmLogistics}
                  mayWritePrice={capabilities.includes("price.input.write")}
                  editing={einkaufEdit} onEditingChange={setEinkaufEdit}
                  steuerung={einkauf}
                  onOpenSupplier={onOpenSupplier ? (sid, fokus) => bewache(() => onOpenSupplier(sid, fokus)) : undefined}
                  i18nLabel={t("log.sectionPurchasing")}
                  attention={<SectionAttention
                    items={a.attention.filter((x) => x.kind !== "gap")}
                    section="einkauf" i18n={i18n} />} />
                </div>
                {capabilities.includes("purchasing.read") && (
                  <div className="ek__rechts">
                    {/* Dieselbe Abschnittszeile wie links -- beide Karten beginnen auf einer Hoehe. */}
                    <h3 className="detail__section">{t("importweg.section")}</h3>
                    <ImportwegPreise articleId={a.id} i18n={i18n} version={preisVersion} />
                  </div>
                )}
                </div>
                {/*
                  * EINKAUF & KALKULATION ALS EINE ARBEITSFLAECHE.
                  *
                  * Der Einkaufspreis steht in der Lieferantenkarte darueber
                  * (dort wird er auch erfasst); der eigene Block
                  * „Einkaufspreis" war dieselbe Zahl ein zweites Mal. Darunter
                  * die Kalkulation als Kette und der Listenpreis mit seinem
                  * Zustand -- nicht mehr drei nummerierte Kapitel.
                  */}
                {capabilities.includes("purchasing.read") && (
                  <CalculationSections articleId={a.id} ansicht="arbeitsflaeche"
                    mayCalculate={capabilities.includes("price.input.write")}
                    mayRelease={capabilities.includes("price.parameter.write")}
                    mayUndercut={capabilities.includes("price.minimum_margin.override")}
                    onPreisGeaendert={() => { kopfNeuLaden(); setPreisVersion((v) => v + 1); }}
                    i18n={i18n} />
                )}
              </>
            ) : open === "modell" ? (
              <>
                <SectionAttention items={a.attention} section="modell" i18n={i18n} />
                <ArticleModelSection article={a} meta={meta}
                  mayWriteModels={mayWriteModels} mayListDte={mayListDte}
                  onChanged={setA} onError={setError} onOpenModel={onOpenModel} />
              </>
            ) : open === "texte" ? (
              <>
                <SectionAttention items={a.attention} section="texte" i18n={i18n} />
                <ArticleTexts articleId={a.id} mayWrite={mayWrite} mayRelease={mayRelease}
                  supplier={a.supplier} shortDescriptionRule={a.shortDescriptionRule} />
              </>
            ) : open === "dokumente" ? (
              <ArticleDocuments articleId={a.id} mayWrite={mayWriteDocuments}
                editing={dokumenteEdit} onEditingChange={setDokumenteEdit}
                steuerung={dokumente} />
            ) : (
              <>
                <SectionAttention items={a.attention} section="herkunft" i18n={i18n} />
                <Provenance a={a} i18n={i18n} />
              </>
            )}
          </div>
        </div>
      )}

      {wartend !== null && (
        <UnsavedDialog i18n={i18n}
          onSave={async () => {
            /*
             * NUR WEITER, WENN WIRKLICH ALLES GESPEICHERT IST.
             *
             * Scheitert ein Wert, bleibt der Dialog nicht stehen -- er
             * schliesst, und der Benutzer landet wieder in dem Bereich, in
             * dem der Fehler an seinem Feld steht. Weiterzugehen hiesse, den
             * Fehler mitzunehmen und die Eingabe zu verlieren.
             *
             * Gefragt werden BEIDE Bereiche. Offen ist in aller Regel nur
             * einer -- ein Bereichswechsel fragt ja gerade nach, bevor der
             * zweite ueberhaupt aufgeht. `?? true` heisst „dort war nichts
             * zu tun" und nicht „dort ist etwas schiefgegangen".
             */
            const a1 = (await technik.current?.save()) ?? true;
            const a2 = (await logistik.current?.save()) ?? true;
            const a3 = (await einkauf.current?.save()) ?? true;
            const a4 = (await dokumente.current?.save()) ?? true;
            setWartend(null);
            if (a1 && a2 && a3 && a4) wartend.run();
          }}
          onDiscard={() => {
            technik.current?.discard(); logistik.current?.discard();
            einkauf.current?.discard(); dokumente.current?.discard();
            setWartend(null); wartend.run();
          }}
          onStay={() => setWartend(null)} />
      )}
    </aside>
  );
}

/** Das Bleistiftzeichen -- als Konstante, damit es kein fester Text ist. */
const PENCIL = "\u270E";

/**
 * Die Rueckfrage vor einem Wechsel.
 *
 * DREI ANTWORTEN, KEINE ZWEI. „Verwerfen oder abbrechen" zwingt zu einer
 * Entscheidung ueber die Arbeit, obwohl die Frage nur der Weg war. Wer
 * speichern will, soll speichern koennen, ohne den Dialog erst wegzuklicken
 * und die Handlung danach zu wiederholen.
 */
function UnsavedDialog({ i18n, onSave, onDiscard, onStay }: {
  i18n: I18n; onSave: () => void; onDiscard: () => void; onStay: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  return (
    <div className="detail__dialogbg" role="presentation">
      <div className="detail__dialog" role="alertdialog" aria-modal="true"
           aria-label={t("tk.leaveTitle")}>
        <h3 className="detail__dialogtitle">{t("tk.leaveTitle")}</h3>
        <p className="detail__dialogtext">{t("tk.leaveText")}</p>
        <div className="detail__dialogactions">
          <button className="btn btn--primary" onClick={onSave}>{t("tk.leaveSave")}</button>
          <button className="btn" onClick={onDiscard}>{t("tk.leaveDiscard")}</button>
          <button className="btn" onClick={onStay}>{t("tk.leaveStay")}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Herkunft und Historie -- hier bleibt die Quellbezeichnung nachvollziehbar.
 *
 * Seit V2 ist das ihr EINZIGER Platz: aus dem Produktkopf ist sie
 * verschwunden, weil „TP154 | L: 154 cm | 18 - 75 W | …" Provenienz ist und
 * keine Produktbezeichnung. Geloescht wurde sie nicht -- sie steht hier
 * unveraendert.
 */
function Provenance({ a, i18n }: { a: Detail; i18n: I18n }): React.JSX.Element {
  const t = i18n.t;
  return (
    <>
    <h3 className="detail__section">{t("prov.section")}</h3>
    <dl className="prov">
      <Row label={t("detail.category")}>{categoryLabel(i18n, a.category.code, a.category.name)}</Row>
      {/* Fachentscheidung 17: aus dem Kopf verschwindet sie, sobald ein
          Artikelname existiert -- hier bleibt sie immer lesbar. */}
      <Row label={t("head.sourceLabel")}>{a.shortDescription ?? <Unknown />}</Row>
      <Row label={t("detail.legacy")}>
        {a.legacyCategoryRaw ? <code>{a.legacyCategoryRaw}</code> : <Unknown />}
      </Row>
      <Row label={t("detail.articleNumbers")}>
        {a.articleNumbers.map((n) => (
          <div key={n.number}>
            <code>{n.number}</code>{" "}
            <span className="prov__kind">{i18n.tCode("numberKind", n.kind)}</span>
          </div>
        ))}
      </Row>
      <Row label={t("detail.quality")}>
        {a.qualityStatus ? i18n.tCode("quality", a.qualityStatus) : <Unknown />}
      </Row>
      <Row label={t("detail.openFlags")}>
        {a.reviewFlags.length === 0
          ? <span className="detail__none">{t("common.none")}</span>
          : <ul className="detail__flags">
              {a.reviewFlags.map((f) => (
                <li key={f.code}>
                  <strong>{i18n.tCode("flag", f.code, f.name)}</strong>{f.note && <> — {f.note}</>}
                </li>
              ))}
            </ul>}
      </Row>
      {a.compatibilities.map((c, i) => (
        <Row key={i} label={t("detail.compatibleWith")}>
          {c.manufacturer}{c.system && <> / {c.system}</>}
        </Row>
      ))}
    </dl>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="prov__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
